import {Box, PopoverVirtualElement} from '@mui/material';
import {ReactComponent as HorizontalResize} from '../../assets/images/prizmdoc-icons/resize-preview.svg';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import './DocumentArea.scss';
import DeleteModal from '../document-viewer/components/delete-modal/DeleteModal';
import {ResultEntry, SelectedMarkPosition} from './hooks/usePrizmDoc';
import {useGetRedactionPanelStatus} from '../../hooks/document/useDocument';
import RedactionNavigationWrapper from '../document-viewer/panels/document-center-panel/redaction-menu/RedactionNavigationWrapper';
import {RedactionPanelStatus} from '../../api/types/document.type';
import {v4 as uuidv4} from 'uuid';
import DocThumbnailSizeControl, {
  ThumbnailSize,
} from '../document-viewer/panels/document-center-panel/thumbnail-size-control/DocThumbnailSizeControl';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {ToastType} from '../../shared/constants';
import ArrToastNotification, {
  ArrStatusToastProps,
} from '../common/arr-toast-notification/ArrToastNotification';
import ReactDOM from 'react-dom';
import {t} from 'i18next';
import PersistentHighlightWrapper from '../document-viewer/panels/document-center-panel/persistent-highlight/PersistentHighlightWrapper';
import {ToolbarActionType} from '../document-viewer/doc-navigation/main-panel-toolbar/MainPanelToolbar';
import {DeleteBatchRedactionPopup} from '../document-viewer/panels/document-center-panel/redaction-menu/DeleteBatchRedactionPopup';
import ReviewerRemarks from '../document-viewer/panels/document-center-panel/reviewer-remarks/ReviewerRemarks';
import BannerAccordion from '../document-viewer/components/banner-accordion/BannerAccordion';
import {ReactComponent as AlertIcon} from '../../../src/assets/images/alert-icon.svg';
import {MouseToolType} from '../document-viewer/docViewerUtils';
export type RedactionTrigger = {
  trigger: boolean;
};

const THUMBNAIL_MIN_WIDTH = 120;
const THUMBNAIL_MEDIUM_WIDTH = 176;
const THUMBNAIL_MAX_WIDTH = 224;
const HUNDRED = 100;
const PX_REM_RATIO = 16;
const THUMBNAIL_SCROLL_TRIGGER_DELAY = 100;
const enDeleteModal = 'docView.deleteModal';

export const DocumentArea = ({
  viewerControl,
  thumbnailControl,
  thumbRef,
  rightPanelRef,
  viewerRef,
  containerRef,
  selectedMark,
  documentId,
  markupLoaded,
  selectedToolbarOption,
  setSelectedMark,
  redactionTriggerState,
  highlightSearchResults,
  markForRemark,
  setCurrentRemarkColor,
  annotationLayerId,
  duplicateDocs,
  connectionId,
  setRemark,
  showWarning,
  emptyConcepts,
  searchLoading,
}: {
  markForRemark: Mark | null;
  thumbRef: React.RefObject<HTMLDivElement | null>;
  rightPanelRef: React.RefObject<HTMLDivElement | null>;
  viewerRef: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLElement | null>;
  viewerControl: ViewerControl | null;
  thumbnailControl?: ThumbnailControl | null;
  selectedMark?: SelectedMarkPosition | null;
  documentId?: number;
  markupLoaded?: MarkUpLoaded | null;
  selectedToolbarOption?: string;
  setSelectedMark?: Dispatch<SetStateAction<SelectedMarkPosition | null>>;
  redactionTriggerState?: RedactionTrigger;
  highlightSearchResults?: Record<string, ResultEntry>;
  setCurrentRemarkColor?: Dispatch<SetStateAction<string>>;
  duplicateDocs?: string[];
  annotationLayerId?: string;
  connectionId?: string;
  setRemark: React.Dispatch<React.SetStateAction<Mark | null>>;
  showWarning?: boolean;
  emptyConcepts?: {description: string; color: string}[];
  searchLoading?: boolean;
}) => {
  const defaultBatchRedactions: {[key: string]: Redaction} = (() => {
    const key1 = `AllBatchRedactions_${uuidv4()}`;

    return {
      [key1]: {
        id: key1,
        term: t('docView.batchRedactions'),
        count: 0,
        currentCounter: -1,
        isDefaultRedaction: true,
        redactions: [],
      },
    };
  })();
  const defaultAllRedactions: {[key: string]: Redaction} = (() => {
    const key1 = `AllRedactions_${uuidv4()}`;

    return {
      [key1]: {
        id: key1,
        term: t('docView.allRedactions'),
        count: 0,
        currentCounter: -1,
        isDefaultRedaction: true,
        redactions: [],
      },
    };
  })();

  const [batchRedactions, setBatchRedactions] = useState<{
    [key: string]: Redaction;
  }>(defaultBatchRedactions);
  const [allRedactions, setAllRedactions] = useState<{
    [key: string]: Redaction;
  }>(defaultAllRedactions);
  const [leftWidth, setLeftWidth] = useState(0); // percentage
  const [thumbnailMinWidth, setThumbnailMinWidth] = useState(0); // percentage

  const [currentMinWidth, setCurrentMinWidth] = useState(THUMBNAIL_MAX_WIDTH);
  const [currentThumbnailSize, setCurrentThumbnailSize] =
    useState<ThumbnailSize>('large');
  const [thumbnailVisible, setThumbnailVisible] = useState(false);
  const thumbnailContainerId = 'thumbnails';

  const activeDoc = useSelector((state: RootState) => state.docView.activeDoc);
  const hasRnfvFiles = useSelector(
    (state: RootState) => state.docView.hasRnfvFiles
  );

  const isImageRedactionsEnabled = useSelector(
    (state: RootState) => state.docView.docViewConfiguration.imageRedactions
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState(ToastType.Success);
  const [showDeleteBatchModal, setShowDeleteBatchModal] =
    useState<boolean>(false);
  const [batchRedactionLabel, setBatchRedactionLabel] = useState<string>('');
  const [isHiddenProperties, setIsHiddenProperties] = useState<boolean>(false);
  const [contentWarningItems, setContentWarningItems] = useState<ReactNode>('');
  const [visibleContent, setVisibleContent] = useState<string>('');

  useEffect(() => {
    const items: React.ReactNode[] = [];
    let message = '';

    if (activeDoc) {
      const excelProtectedSheetsFlag = !!Number(
        activeDoc.__InternalExcelProtectedSheets
      );
      const excelProtectedWorkbookFlag = !!Number(
        activeDoc.__InternalExcelProtectedWorkbook
      );
      const hiddenProperties = Number(activeDoc.__InternalHiddenProperties);
      const internalHiddenPropertiesText = String(
        activeDoc.__InternalHiddenPropertiesText ?? ''
      );

      switch (hiddenProperties) {
        case 1:
          message = t('docView.headers.contentWarning.title.message1');
          break;
        case 2:
          message = t('docView.headers.contentWarning.title.message2');
          break;
        case 3:
          message = t('docView.headers.contentWarning.title.message3');
          break;
        default:
          message = '';
      }

      if (internalHiddenPropertiesText !== '') {
        items.push(
          <li
            className="typo-text body3_size"
            key={t('docView.headers.contentWarning.text.hiddenProperties')}
            data-testid={t(
              'docView.headers.contentWarning.text.hiddenProperties'
            )}
          >
            {internalHiddenPropertiesText}
          </li>
        );
      }

      if (excelProtectedSheetsFlag) {
        items.push(
          <li
            className="typo-text body3_size"
            key={t('docView.headers.contentWarning.text.excelProtectedSheets')}
            data-testid={t(
              'docView.headers.contentWarning.text.excelProtectedSheets'
            )}
          >
            {t('docView.headers.contentWarning.excelProtectedSheets')}
          </li>
        );
      }

      if (excelProtectedWorkbookFlag) {
        items.push(
          <li
            className="typo-text body3_size"
            key={t(
              'docView.headers.contentWarning.text.excelProtectedWorkbook'
            )}
            data-testid={t(
              'docView.headers.contentWarning.text.excelProtectedWorkbook'
            )}
          >
            {t('docView.headers.contentWarning.excelProtectedWorkbook')}
          </li>
        );
      }
    }

    if (items.length > 0 || message !== '') setIsHiddenProperties(true);

    const contentWarningItems = (
      <ul
        className="typo-text body3_size"
        data-testid={`banner-list-${documentId}`}
      >
        {items}
      </ul>
    );
    setContentWarningItems(contentWarningItems);
    setVisibleContent(message);
  }, [documentId, activeDoc]);

  const {mutate: fetchRedactionPanelStatus} = useGetRedactionPanelStatus(
    (data: RedactionPanelStatus[]) => {
      readRedactionMarks(data);
    }
  );

  const SuccessToast = useCallback((props: ArrStatusToastProps) => {
    return ReactDOM.createPortal(
      <ArrToastNotification {...props} />,
      document.body
    );
  }, []);

  const triggerToast = (
    toastType: ToastType | ((prevState: ToastType) => ToastType),
    toastMessage: string
  ) => {
    setShowToast(true);
    setDeleteAnchorEl(null);
    setShowDeleteBatchModal(false);
    setToastType(toastType);
    setToastMessage(toastMessage);
  };

  const [noHitTerms, setNoHitTerms] = useState<RedactionPanelStatus[]>([]);
  const [partialHitTerms, setPartialHitTerms] = useState<
    RedactionPanelStatus[]
  >([]);
  useEffect(() => {
    if (containerRef) {
      const containerWidth = containerRef?.current?.offsetWidth;

      if (containerWidth) {
        setLeftWidth((THUMBNAIL_MAX_WIDTH / containerWidth) * 100);
        setThumbnailMinWidth((THUMBNAIL_MAX_WIDTH / containerWidth) * 100);
      }
    }
  }, [containerRef]);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<null | Omit<
    PopoverVirtualElement,
    'nodeType'
  >>(null);

  useEffect(() => {
    if (selectedMark) {
      setDeleteAnchorEl({
        getBoundingClientRect: () => ({
          x: selectedMark.x,
          y: selectedMark.y,
          height: 0,
          width: 0,
          bottom: selectedMark.y,
          left: selectedMark.x,
          right: selectedMark.y,
          top: selectedMark.y,
          toJSON: () => {},
        }),
      });
    } else {
      setDeleteAnchorEl(null);
    }
  }, [selectedMark]);

  const redactionTypes = new Set([
    'TextSelectionRedaction',
    'RectangleRedaction',
    'TextRedaction',
  ]);
  const readRedactionMarks = (partialOrNoHitTerms: RedactionPanelStatus[]) => {
    const marks = viewerControl?.getAllMarks() ?? [];
    const filteredMarks = marks.filter((mark) =>
      redactionTypes.has(mark?.getType?.() ?? mark?.type)
    );
    marks.forEach((mark) => {
      mark.interactionMode =
        isImageRedactionsEnabled && !hasRnfvFiles
          ? 'Full'
          : 'SelectionDisabled';
    });
    filteredMarks.sort((r1, r2) => {
      if (r1.pageNumber !== r2.pageNumber) {
        return r1.pageNumber - r2.pageNumber;
      }

      if (r1.type !== r2.type) {
        return r1.type.localeCompare(r2.type);
      }

      if (r1.type === 'TextSelectionRedaction') {
        return r1.getPosition().startIndex - r2.getPosition().startIndex;
      }

      if (r1.type === 'RectangleRedaction') {
        const yDiff = r1.getBoundingRectangle().y - r2.getBoundingRectangle().y;
        return yDiff !== 0
          ? yDiff
          : r1.getBoundingRectangle().x - r2.getBoundingRectangle().x;
      }

      return 0;
    });

    processPersistentRedactions(filteredMarks, partialOrNoHitTerms);
  };

  const processPersistentRedactions = (
    allMarks: Mark[],
    partialOrNoHitTerms: RedactionPanelStatus[]
  ) => {
    function filterToBatchOrTextRedactions(redaction: Mark) {
      return (
        redaction.getType() === 'RectangleRedaction' &&
        Object.prototype.hasOwnProperty.call(
          redaction.getData(),
          'RedactedText'
        )
      );
    }

    const textOrBatchRedactions = allMarks.filter(
      filterToBatchOrTextRedactions
    );
    let redactionMap = new Map<string, Redaction>();

    for (const mark of textOrBatchRedactions) {
      const term = mark.getData().RedactedText;
      if (!redactionMap.has(term)) {
        redactionMap.set(term, {
          id: term,
          term,
          count: 1,
          currentCounter: -1,
          redactions: [mark],
        });
      } else {
        const existing = redactionMap.get(term)!;
        redactionMap.set(term, {
          ...existing,
          count: existing.count + 1,
          currentCounter: -1,
          redactions: [...existing.redactions, mark],
        });
      }
    }

    const defaultRedactions = Object.values(batchRedactions);
    defaultRedactions[0].count = textOrBatchRedactions.length;
    defaultRedactions[0].redactions = textOrBatchRedactions;
    redactionMap = new Map([
      [defaultRedactions[0].id, defaultRedactions[0]],
      ...redactionMap,
    ]);

    const defaultAllRedactions = Object.values(allRedactions);
    defaultAllRedactions[0].count = allMarks.length;
    // Sort by time in descending order. The time is embedded in the ID
    defaultAllRedactions[0].redactions = allMarks.sort(
      (a: any, b: any) => Number(b.getData()['ID']) - Number(a.getData()['ID'])
    );

    const noHitTerms = partialOrNoHitTerms.filter(
      (t: RedactionPanelStatus) => t.isPartial === false
    );

    const partialHitTerms = partialOrNoHitTerms.filter(
      (t: RedactionPanelStatus) => t.isPartial === true
    );
    setNoHitTerms(noHitTerms);
    setPartialHitTerms(partialHitTerms);
    setBatchRedactions(Object.fromEntries(redactionMap));
    setAllRedactions(
      Object.fromEntries([
        [
          defaultAllRedactions[0].id,
          {...defaultAllRedactions[0], currentCounter: -1},
        ],
      ])
    );
  };

  const handleBatchNavigation = (index: number, value: Redaction) => {
    for (const t of Object.values(batchRedactions)) {
      t.currentCounter = -1;
    }
    for (const t of Object.values(allRedactions)) {
      t.currentCounter = -1;
    }

    viewerControl?.deselectAllMarks();
    if (index > 0) {
      value.currentCounter = index - 1;
      const mark = value.redactions[index - 1];
      viewerControl?.selectMarks([mark]);
      viewerControl?.scrollTo(mark);
      const viewerRect = viewerRef.current?.getBoundingClientRect();
      setSelectedMark?.({
        x: viewerRect
          ? viewerRect.left + viewerRect.width / 2
          : window.innerWidth / 2,
        y: viewerRect
          ? viewerRect.top + viewerRect.height / 2
          : window.innerHeight / 2,
        mark,
      });
    } else {
      setSelectedMark?.(null);
      setDeleteAnchorEl(null);
    }

    setBatchRedactions((prev) => ({...prev}));
    setAllRedactions((prev) => ({...prev}));
  };

  useEffect(() => {
    if (!documentId || !markupLoaded) return;
    fetchRedactionPanelStatus(documentId);
  }, [documentId, markupLoaded]);

  const deleteMark = useCallback(() => {
    if (viewerControl) {
      if (batchRedactionLabel && batchRedactions) {
        const selectedBatchRedactions =
          Object.values(batchRedactions).find(
            (g) => g.term.toLowerCase() === batchRedactionLabel.toLowerCase()
          )?.redactions ?? [];
        viewerControl.deleteMarks(selectedBatchRedactions);
        fetchRedactionPanelStatus(documentId ?? 0);
        triggerToast(
          ToastType.Success,
          t(`${enDeleteModal}.deleteRedactionTagSuccess`)
        );
      } else {
        const type = viewerControl
          .getSelectedMarks()[0]
          .type.toLocaleLowerCase();
        viewerControl.deleteMarks(viewerControl.getSelectedMarks());
        fetchRedactionPanelStatus(documentId ?? 0);
        if (type === 'rectangleannotation') {
          triggerToast(
            ToastType.Success,
            t(`${enDeleteModal}.deleteAnnotationSuccess`)
          );
        } else if (type === 'highlightannotation') {
          viewerControl?.deselectAllMarks();
          setRemark(null);
          viewerControl &&
            viewerControl.setCurrentMouseTool(
              MouseToolType.HighlightAnnotation
            );
        } else {
          triggerToast(
            ToastType.Success,
            t(`${enDeleteModal}.deleteRedactionTagSuccess`)
          );
        }
      }
    }
  }, [viewerControl, batchRedactionLabel, batchRedactions]);

  const parentRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!containerRef.current) return;

      e.preventDefault();

      startXRef.current = e.clientX;
      startWidthRef.current = leftWidth;
      isDraggingRef.current = true;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const viewer = document.querySelector('.pcc-viewer');
      if (viewer) (viewer as HTMLElement).style.pointerEvents = 'none';
    },
    [containerRef, leftWidth]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!containerRef.current) return;
      if (!isDraggingRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const delta = e.clientX - startXRef.current;

      const newLeftWidth =
        (((startWidthRef.current / HUNDRED) * containerWidth + delta) /
          containerWidth) *
        HUNDRED;

      // Clamp between 10% and 90%
      setLeftWidth(
        Math.min(THUMBNAIL_MAX_WIDTH, Math.max(thumbnailMinWidth, newLeftWidth))
      );
    },
    [containerRef, thumbnailMinWidth]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      isDraggingRef.current = false;
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);

      document.body.style.cursor = 'default';
      document.body.style.userSelect = '';

      const viewer = document.querySelector('.pcc-viewer');
      if (viewer) (viewer as HTMLElement).style.pointerEvents = '';

      viewerControl?.fitContent(window.PCCViewer.FitType.FullWidth);
    },
    [viewerControl]
  );

  const pxToRem = (px: number) => px / PX_REM_RATIO;

  const setThumbnailWidthValues = (thumbnailSize: ThumbnailSize) => {
    if (containerRef) {
      const containerWidth = containerRef?.current?.offsetWidth;
      if (containerWidth) {
        setCurrentThumbnailSize(thumbnailSize);
        if (thumbnailSize == 'large') {
          setCurrentMinWidth(THUMBNAIL_MAX_WIDTH);
          setThumbnailMinWidth((THUMBNAIL_MAX_WIDTH / containerWidth) * 100);
        }
        if (thumbnailSize == 'medium') {
          setCurrentMinWidth(THUMBNAIL_MEDIUM_WIDTH);
          setThumbnailMinWidth((THUMBNAIL_MEDIUM_WIDTH / containerWidth) * 100);
        }
        if (thumbnailSize == 'small') {
          setCurrentMinWidth(THUMBNAIL_MIN_WIDTH);
          setThumbnailMinWidth((THUMBNAIL_MIN_WIDTH / containerWidth) * 100);
        }
      }
      refreshThumbPanel();
      setTimeout(() => {
        thumbnailControl?.scrollTo(viewerControl?.getPageNumber() ?? 0, {
          forceAlignTop: true,
        });
      }, THUMBNAIL_SCROLL_TRIGGER_DELAY);
    }
  };

  const refreshThumbPanel = () => {
    const thumbnailDiv = document.getElementById('thumbnails');
    const resizerDiv = document.getElementById('prizmdoc-resizer');

    if (thumbnailDiv && resizerDiv) {
      thumbnailDiv.style.display = 'none';
      resizerDiv.style.display = 'none';
      window.dispatchEvent(new Event('resize'));
      viewerControl &&
        viewerControl?.fitContent(window.PCCViewer.FitType.FullWidth);

      thumbnailDiv.style.display = 'block';
      resizerDiv.style.display = 'flex';
      window.dispatchEvent(new Event('resize'));
      viewerControl &&
        viewerControl?.fitContent(window.PCCViewer.FitType.FullWidth);
    }
  };

  useEffect(() => {
    const thumbnailDiv = document.getElementById(thumbnailContainerId);
    if (thumbnailDiv) {
      thumbnailDiv.style.display = 'none';
    }
    setCurrentMinWidth(THUMBNAIL_MAX_WIDTH);
    setCurrentThumbnailSize('large');
    setThumbnailVisible(false);
  }, [activeDoc]);

  useEffect(() => {
    const thumbPanel = document.getElementById(thumbnailContainerId);
    setThumbnailVisible(false);
    if (!thumbPanel) return;

    const observer = new MutationObserver(() => {
      const isVisible = window.getComputedStyle(thumbPanel).display !== 'none';
      if (isVisible) {
        setThumbnailVisible(true);
      } else {
        setThumbnailVisible(false);
      }
    });

    observer.observe(thumbPanel, {
      attributes: true,
      attributeFilter: ['style'],
    });

    if (window.getComputedStyle(thumbPanel).display !== 'none') {
      setThumbnailVisible(true);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (redactionTriggerState?.trigger && documentId != null) {
      fetchRedactionPanelStatus(documentId ?? 0);
    }
  }, [redactionTriggerState, markupLoaded]);
  const onNavigateResult = useCallback(
    (result: SearchResult | null) => {
      // Navigating to a result while a search is still in-flight can collide
      // with PrizmDoc's internal search state and crash the viewer.
      if (viewerControl && !searchLoading) {
        viewerControl.setSelectedSearchResult(result, true);
      }
    },
    [viewerControl, searchLoading]
  );

  return (
    <>
      <Box
        sx={{
          width: `${leftWidth}% !important`,
          maxWidth: `${pxToRem(THUMBNAIL_MAX_WIDTH)}rem`,
          minWidth: `${pxToRem(currentMinWidth)}rem`,
          display: `${thumbnailVisible ? 'block' : 'none'}`,
        }}
      >
        <DocThumbnailSizeControl
          currentSize={currentThumbnailSize}
          onSizeChange={setThumbnailWidthValues}
        />
        <Box
          id="thumbnails"
          ref={thumbRef}
          className={`prizmdoc-thumbnails doc-thumbnail-container ${currentThumbnailSize}`}
          style={{
            display: 'none',
          }}
        ></Box>
      </Box>
      <Box
        id="prizmdoc-resizer"
        className="prizmdoc-document-resizer"
        style={{
          display: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <HorizontalResize />
      </Box>

      <Box
        className="prizmdoc-viewer-wrapper"
        sx={{width: `${HUNDRED - leftWidth}% !important`}}
      >
        {isHiddenProperties && (
          <BannerAccordion
            variant="warning"
            startIcon={<AlertIcon />}
            visibleContent={visibleContent}
            expandedContent={contentWarningItems}
            dataTestId="docView-banner"
          />
        )}
        <Box id="prizmViewer" ref={viewerRef} className="prizmdoc-viewer" />
      </Box>

      <Box
        id="right-panel"
        ref={rightPanelRef}
        className="prizmdoc-right-panel"
        style={{
          display:
            selectedToolbarOption === ToolbarActionType.Redact ||
            selectedToolbarOption === ToolbarActionType.PersistetHighlight ||
            selectedToolbarOption === ToolbarActionType.Remark
              ? 'block'
              : 'none',
        }}
      >
        {selectedToolbarOption === ToolbarActionType.Redact && (
          <RedactionNavigationWrapper
            allRedactions={allRedactions}
            batchRedactions={batchRedactions}
            partialHitTerms={partialHitTerms}
            noHitTerms={noHitTerms}
            onBatchNavigation={handleBatchNavigation}
            setShowDeleteBatchModal={setShowDeleteBatchModal}
            setBatchRedactionLabel={setBatchRedactionLabel}
          />
        )}
        {selectedToolbarOption === ToolbarActionType.PersistetHighlight && (
          <PersistentHighlightWrapper
            highlightOptions={highlightSearchResults}
            onNavigateResult={onNavigateResult}
            emptyConcepts={emptyConcepts ?? []}
            disableNavigation={searchLoading}
          />
        )}
        {selectedToolbarOption === ToolbarActionType.Remark &&
          documentId !== undefined &&
          viewerControl && (
            <ReviewerRemarks
              viewerControl={viewerControl}
              docId={documentId}
              markForRemark={markForRemark}
              setCurrentRemarkColor={setCurrentRemarkColor}
              connectionId={connectionId}
              annotationLayerId={annotationLayerId}
              duplicateDocs={duplicateDocs}
              setRemark={setRemark}
              disabled={!!showWarning}
            />
          )}
      </Box>
      {deleteAnchorEl && isImageRedactionsEnabled && !hasRnfvFiles && (
        <DeleteModal
          anchorElement={deleteAnchorEl}
          isReduction={
            selectedMark?.mark?.type.toLocaleLowerCase() ===
              'rectangleredaction' ||
            selectedMark?.mark?.type.toLocaleLowerCase() ===
              'textselectionredaction' ||
            selectedMark?.mark?.type.toLocaleLowerCase() === 'textredaction'
          }
          onClose={() => {
            setDeleteAnchorEl(null);
          }}
          parentRef={parentRef}
          deleteMark={deleteMark}
          selectedMark={selectedMark ?? null}
          triggerToast={triggerToast}
        />
      )}
      {showDeleteBatchModal && (
        <DeleteBatchRedactionPopup
          closeDeleteBatchRedactionPopup={() => setShowDeleteBatchModal(false)}
          deleteBatchRedaction={deleteMark}
          deleteBatchRedactionLabel={batchRedactionLabel}
        />
      )}
      {showToast && (
        <SuccessToast
          message={toastMessage}
          onClose={() => setShowToast(false)}
          status={[toastType]}
        />
      )}
    </>
  );
};
